import React, {useEffect, useState} from 'react';
import {Button, Card, Checkbox, Form, Image, Input, Modal, Steps, Typography, InputNumber, Alert, Divider, Space} from 'antd';
import {useTheme} from "@/components/common/ThemeProvider";
import {showErrorNotification, showSuccessNotification} from '@/components/common/utils/notify';
import {SelfInvitationRequestDTO} from '@/shared/api/model';
import {
    useRegistrationDomains,
    useSelfInvitation,
    useSendPhoneOtp,
    useVerifyPhoneOtp
} from "@/hooks/invitations/invitations";
import {Link, useNavigate} from "react-router-dom";
import {useCompany} from "@/hooks/company/use-company";
import { useGetLoginLogo } from "@/shared/api/client/appearance-controller";
import { ExclamationCircleOutlined, InfoCircleOutlined, CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { useGetActiveConfig } from '@/modules/onboarding/hooks/useSelfOnboardingConfig';

const { Title, Paragraph, Text } = Typography;
const { Step } = Steps;
const { confirm } = Modal;

interface FormValues extends SelfInvitationRequestDTO {
    terms: boolean;
    otp?: string;
}

const SelfInvitationForm: React.FC = () => {
    const navigate = useNavigate();
    const [form] = Form.useForm<FormValues>();
    const { isLoading, data: companyData } = useCompany();
    const { dark } = useTheme();
    const [isTermsModalVisible, setIsTermsModalVisible] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [isOtpSent, setIsOtpSent] = useState(false);
    const [phoneVerified, setPhoneVerified] = useState(false);
    const [registrationToken, setRegistrationToken] = useState<string>("");
    const [phoneNumber, setPhoneNumber] = useState<string>("");
    const [eligibilityStatus, setEligibilityStatus] = useState<{
        isEligible: boolean | null;
        meetsMinimum: boolean;
        meetsOneThirdRule: boolean;
    }>({ isEligible: null, meetsMinimum: false, meetsOneThirdRule: false });

    const selfInvitationMutation = useSelfInvitation();
    const sendOtpMutation = useSendPhoneOtp();
    const verifyOtpMutation = useVerifyPhoneOtp();

    const { data: selfOnboardingConfig } = useGetActiveConfig();

    const [shouldNavigate, setShouldNavigate] = useState(false);
    const { data: loginLogo } = useGetLoginLogo();
    const logoSrc = typeof loginLogo === 'string' ? loginLogo : loginLogo?.data || '/images/login.png';
    const MINIMUM_MONTHLY_SAVINGS = selfOnboardingConfig?.minimumMonthlyContribution ?? 50000;
    const SHARE_CAPITAL_REQUIRED = 500000;

    // Handle navigation with useEffect
    useEffect(() => {
        let timeoutId: NodeJS.Timeout;

        if (shouldNavigate) {
            timeoutId = setTimeout(() => {
                navigate('/register', {
                    replace: true,
                    state: {
                        fromInvitation: true,
                        message: 'invitation_sent'
                    }
                });
            }, 1500);
        }

        // Cleanup function to clear timeout if component unmounts
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [shouldNavigate, navigate]);

    const showTerms = () => {
        setIsTermsModalVisible(true);
    };

    const calculateEligibility = (values: { grossSalary?: number; netSalary?: number }) => {
        const { grossSalary, netSalary } = values;

        if (!grossSalary || !netSalary) {
            return { isEligible: null, meetsMinimum: false, meetsOneThirdRule: false };
        }

        // Check if can contribute minimum monthly savings amount
        const meetsMinimum = netSalary >= MINIMUM_MONTHLY_SAVINGS;

        // Check one-third rule
        const remainingAfterDeduction = netSalary - MINIMUM_MONTHLY_SAVINGS;
        const meetsOneThirdRule = remainingAfterDeduction >= (grossSalary / 3);

        return {
            isEligible: meetsMinimum && meetsOneThirdRule,
            meetsMinimum,
            meetsOneThirdRule
        };
    };

    const handleEligibilityChange = () => {
        const values = form.getFieldsValue();
        const { grossSalary, netSalary } = values;

        if (grossSalary && netSalary) {
            const status = calculateEligibility({ grossSalary, netSalary });
            setEligibilityStatus(status);
        }
    };

    const handleSendOtp = async () => {
        try {
            const phoneNumber = form.getFieldValue('phoneNumber');
            if (!phoneNumber) {
                showErrorNotification('Error', 'Please enter your phone number', dark);
                return;
            }

            const {registrationToken} = await sendOtpMutation.mutateAsync(phoneNumber);
            if(registrationToken){
                setRegistrationToken(registrationToken);
                setPhoneNumber(phoneNumber);
            }
            setIsOtpSent(true);
            showSuccessNotification('OTP Sent', 'Please check your phone for verification code', dark);
        } catch (error) {
            showErrorNotification('Error', 'Failed to send verification code', dark);
        }
    };

    const handleVerifyOtp = async () => {
        try {
            const phoneNumber = form.getFieldValue('phoneNumber');
            const otp = form.getFieldValue('otp');

            if (!otp) {
                showErrorNotification('Error', 'Please enter verification code', dark);
                return;
            }

            const isVerified = await verifyOtpMutation.mutateAsync({
                phoneNumber,
                registrationToken,
                otp
            });

            if (isVerified) {
                setPhoneVerified(true);
                setCurrentStep(currentStep + 1);
                showSuccessNotification('Success', 'Phone number verified successfully', dark);
            } else {
                showErrorNotification('Error', 'Invalid verification code', dark);
            }
        } catch (error) {
            showErrorNotification('Error', 'Failed to verify code', dark);
        }
    };

    const handleSubmit = async (values: FormValues) => {
        // Eligibility Step
        if (currentStep === 0) {
            if (!values.governmentEmployee) {
                Modal.error({
                    title: 'Eligibility Error',
                    content: 'Only government employees are eligible to register with Hazina SACCOS.',
                });
                return;
            }

            const status = calculateEligibility(values);

            if (!status.isEligible) {
                confirm({
                    title: 'Eligibility Warning',
                    icon: <ExclamationCircleOutlined />,
                    content:
                        'Based on the information provided, you may not meet the eligibility requirements for Hazina SACCOS membership. ' +
                        'If you proceed with registration, there may be charges that are not fully refundable. ' +
                        'Your information will be sent to Hazina SACCOS for review. Do you wish to proceed?',
                    onOk() {
                        setCurrentStep(currentStep + 1);
                    },
                });
            } else {
                setCurrentStep(currentStep + 1);
            }
            return;
        }

        // Phone Verification Step
        if (currentStep === 1) {
            return; // Phone verification handled separately by handleVerifyOtp
        }

        // Final Submission
        if (currentStep === 2) {
            if (!phoneVerified) {
                showErrorNotification('Error', 'Please verify your phone number first', dark);
                return;
            }

            const governmentEmployee = form.getFieldValue('governmentEmployee');
            const netSalary = form.getFieldValue('netSalary');
            const grossSalary = form.getFieldValue('grossSalary');
            const termsAccepted = form.getFieldValue('termsAccepted');

            try {
                const request: SelfInvitationRequestDTO = {
                    fullName: values.fullName,
                    email: values.email,
                    phoneNumber: phoneNumber,
                    governmentEmployee: governmentEmployee || false,
                    grossSalary: grossSalary,
                    termsAccepted: termsAccepted || false,
                    netSalary: netSalary
                };

                const result = await selfInvitationMutation.mutateAsync(request);

                if (result) {
                    setShouldNavigate(true);
                    form.resetFields();
                    showSuccessNotification(
                        'Invitation Requested',
                        'Please check your email for the invitation code.',
                        dark
                    );
                } else {
                    showErrorNotification(
                        'Request Failed',
                        'Failed to process invitation request.',
                        dark
                    );
                }
            } catch (error) {
                showErrorNotification(
                    'Error',
                    error instanceof Error ? error.message : 'An unexpected error occurred',
                    dark
                );
            }
        }
    };

    const steps = [
        {
            title: 'Eligibility Check',
            content: (
                <div className="space-y-6">
                    <div className="text-center mb-6">
                        <Title level={4} className={dark ? 'text-white' : ''}>
                            Hazina SACCOS Eligibility Check
                        </Title>
                        <Text className={dark ? 'text-gray-300' : 'text-gray-600'}>
                            Please provide the following information to check your eligibility
                        </Text>
                    </div>

                    <Alert
                        message="Hazina SACCOS Membership Requirements"
                        description={
                            <ul className="list-disc pl-5 mt-2">
                                <li><strong>Government Employment:</strong> Only government employees are eligible for membership</li>
                                <li><strong>Share Capital:</strong> Required to purchase shares worth TSH 500,000 upon joining</li>
                                <li><strong>Monthly Savings:</strong> Compulsory monthly savings of TSH 50,000 through automatic salary deduction</li>
                                <li><strong>One-Third Rule:</strong> After all deductions, you must remain with at least 1/3 of your gross salary</li>
                                <li><strong>Loan Eligibility:</strong> Access to various loan products based on "cooperative societies policies" compliance</li>
                            </ul>
                        }
                        type="info"
                        showIcon
                        icon={<InfoCircleOutlined />}
                        className="mb-6"
                    />

                    <Form.Item
                        name="governmentEmployee"
                        valuePropName="checked"
                        rules={[{ required: true, transform: value => !!value, message: 'Please confirm your employment status' }]}
                    >
                        <Checkbox className={dark ? 'text-white' : ''}>
                            I confirm that I am a government employee with valid employment credentials
                        </Checkbox>
                    </Form.Item>

                    <Form.Item
                        name="grossSalary"
                        label={<span className={dark ? 'text-white' : ''}>Gross Monthly Salary (TZS)</span>}
                        rules={[
                            { required: true, message: 'Please enter your gross monthly salary' },
                            { type: 'number', min: 1, message: 'Please enter a valid amount' }
                        ]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={value => value!.replace(/\$\s?|(,*)/g, '')}
                            placeholder="Enter your gross monthly salary"
                            className={dark ? 'bg-gray-700 text-white' : ''}
                            onChange={handleEligibilityChange}
                        />
                    </Form.Item>

                    <Form.Item
                        name="netSalary"
                        label={<span className={dark ? 'text-white' : ''}>Net Monthly Salary (after deductions) (TZS)</span>}
                        rules={[
                            { required: true, message: 'Please enter your net monthly salary' },
                            { type: 'number', min: 1, message: 'Please enter a valid amount' },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('grossSalary') >= value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('Net salary cannot be greater than gross salary'));
                                },
                            }),
                        ]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={value => value!.replace(/\$\s?|(,*)/g, '')}
                            placeholder="Enter your net monthly salary"
                            className={dark ? 'bg-gray-700 text-white' : ''}
                            onChange={handleEligibilityChange}
                        />
                    </Form.Item>

                    {eligibilityStatus.isEligible !== null && (
                        <Alert
                            message={eligibilityStatus.isEligible ? "You meet the eligibility requirements" : "You may not meet the eligibility requirements"}
                            description={
                                <div>
                                    <p><strong>Financial Requirements Assessment:</strong></p>
                                    <ul className="list-disc pl-5 mt-2">
                                        <li>
                                            <strong>Share Capital (TSH 500,000):</strong> Required upon joining
                                        </li>
                                        <li>
                                            <strong>Monthly Savings (TSH 50,000):</strong>
                                            <Text type={eligibilityStatus.meetsMinimum ? "success" : "danger"}>
                                                {" " + (eligibilityStatus.meetsMinimum ? "Can afford" : "Cannot afford")}
                                            </Text>
                                        </li>
                                        <li>
                                            <strong>One-third rule requirement:</strong>
                                            <Text type={eligibilityStatus.meetsOneThirdRule ? "success" : "danger"}>
                                                {" " + (eligibilityStatus.meetsOneThirdRule ? "Met" : "Not Met")}
                                            </Text>
                                        </li>
                                    </ul>
                                    {!eligibilityStatus.isEligible && (
                                        <p className="mt-2 text-orange-600">
                                            <strong>Warning:</strong> If you proceed, your application will be subject to review by Hazina SACCOS,
                                            and there may be non-refundable charges.
                                        </p>
                                    )}
                                </div>
                            }
                            type={eligibilityStatus.isEligible ? "success" : "warning"}
                            showIcon
                            className="mb-6"
                        />
                    )}

                    <Form.Item
                        name="termsAccepted"
                        valuePropName="checked"
                        rules={[
                            {
                                validator: (_, value) =>
                                    value ? Promise.resolve() : Promise.reject(new Error('You must accept the terms and conditions'))
                            }
                        ]}
                    >
                        <Checkbox className={dark ? 'text-white' : ''}>
                            I have read and agree to the <Button type="link" onClick={showTerms} className="p-0">Terms and Conditions</Button>
                        </Checkbox>
                    </Form.Item>
                </div>
            )
        },
        {
            title: 'Verify Phone',
            content: (
                <>
                    <Form.Item
                        name="phoneNumber"
                        label={<span className={dark ? 'text-gray-100' : 'text-gray-800'}>Phone Number</span>}
                        rules={[
                            { required: true, message: 'Please enter your phone number' },
                            {
                                pattern: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
                                message: 'Please enter a valid phone number'
                            }
                        ]}
                    >

                        <Input
                            size="large"
                            placeholder="255712345678"
                            className={dark ? 'bg-gray-700 text-white border-gray-600' : ''}
                            disabled={isOtpSent}
                            suffix={
                                <Button
                                    type="link"
                                    onClick={handleSendOtp}
                                    loading={sendOtpMutation.isPending}
                                    disabled={isOtpSent && phoneVerified}
                                >
                                    {isOtpSent ? 'Resend Code' : 'Send Code'}
                                </Button>
                            }
                        />
                    </Form.Item>

                    {isOtpSent && !phoneVerified && (
                        <>
                            <Form.Item
                                name="otp"
                                label={<span className={dark ? 'text-gray-100' : 'text-gray-800'}>Verification Code</span>}
                                rules={[{ required: true, message: 'Please enter the verification code' }]}
                            >
                                <Input
                                    size="large"
                                    placeholder="Enter verification code"
                                    className={dark ? 'bg-gray-700 text-white border-gray-600' : ''}
                                    maxLength={6}
                                />
                            </Form.Item>

                            <Button
                                type="primary"
                                onClick={handleVerifyOtp}
                                loading={verifyOtpMutation.isPending}
                                block
                            >
                                Verify Code
                            </Button>
                        </>
                    )}
                </>
            )
        },
        {
            title: 'Complete Invitation',
            content: (
                <>
                    <Alert
                        className="mb-6"
                        message="Eligibility Status Summary"
                        description={
                            <div>
                                <p><strong>Government Employee:</strong> {form.getFieldValue('governmentEmployee') ? 'Yes ✓' : 'No ✗'}</p>
                                <p><strong>Gross Salary:</strong> {new Intl.NumberFormat('en-TZ').format(form.getFieldValue('grossSalary') || 0)} TZS</p>
                                <p><strong>Net Salary:</strong> {new Intl.NumberFormat('en-TZ').format(form.getFieldValue('netSalary') || 0)} TZS</p>
                                <p><strong>Share Capital Required:</strong> {new Intl.NumberFormat('en-TZ').format(SHARE_CAPITAL_REQUIRED)} TZS</p>
                                <p><strong>Monthly Savings Required:</strong> {new Intl.NumberFormat('en-TZ').format(MINIMUM_MONTHLY_SAVINGS)} TZS</p>
                                <p>
                                    <strong>Eligibility Status:</strong>
                                    <Text type={eligibilityStatus.isEligible ? "success" : "warning"}>
                                        {eligibilityStatus.isEligible ? ' Fully Eligible ✓' : ' Subject to Review ⚠️'}
                                    </Text>
                                </p>
                            </div>
                        }
                        type={eligibilityStatus.isEligible ? "success" : "warning"}
                        showIcon
                    />

                    <Form.Item
                        name="fullName"
                        label={<span className={dark ? 'text-gray-100' : 'text-gray-800'}>Full Name</span>}
                        rules={[
                            { required: true, message: 'Please enter your full name' },
                            { min: 3, message: 'Name must be at least 3 characters' }
                        ]}
                    >
                        <Input
                            size="large"
                            autoComplete="off"
                            placeholder="Enter your full name"
                            className={dark ? 'bg-gray-700 text-white border-gray-600' : ''}
                        />
                    </Form.Item>

                    <Form.Item
                        name="email"
                        label={<span className={dark ? 'text-gray-100' : 'text-gray-800'}>Email Address</span>}
                        rules={[
                            { required: true, message: 'Please enter your email address' },
                            { type: 'email', message: 'Please enter a valid email address' }
                        ]}
                    >
                        <Input
                            size="large"
                            autoComplete="off"
                            placeholder="your@email.com"
                            className={dark ? 'bg-gray-700 text-white border-gray-600' : ''}
                        />
                    </Form.Item>

                    <Form.Item
                        name="terms"
                        valuePropName="checked"
                        rules={[
                            {
                                validator: (_, value) =>
                                    value ? Promise.resolve() : Promise.reject(new Error('You must accept the terms and conditions'))
                            }
                        ]}
                    >
                        <Checkbox className={dark ? 'text-gray-100' : 'text-gray-800'}>
                            I agree to the <Button type="link" onClick={showTerms} className="p-0">Terms and Conditions</Button>
                        </Checkbox>
                    </Form.Item>

                    <Form.Item>
                        {shouldNavigate ? (
                            <Button
                                type="primary"
                                size="large"
                                block
                                onClick={() => navigate('/register', {
                                    replace: true,
                                    state: {
                                        fromInvitation: true,
                                        message: 'invitation_sent'
                                    }
                                })}
                                className={`${dark ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'}`}
                            >
                                Continue to Registration
                            </Button>
                        ) : (
                            <Button
                                type="primary"
                                htmlType="submit"
                                size="large"
                                block
                                loading={selfInvitationMutation.isPending}
                                className={`${dark ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'}`}
                            >
                                Request Invitation
                            </Button>
                        )}
                    </Form.Item>
                </>
            )
        }
    ];

    return (
        <div className={`min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 ${dark ? 'bg-gray-900' : 'bg-slate-700 bg-opacity-90'}`}>
            <div className="max-w-2xl w-full space-y-8">
                <Card
                    className={`shadow-2xl ${dark ? 'bg-gray-800' : 'bg-white'} w-full`}
                    styles={{ body: { padding: '2rem' } }}
                >
                    <div className="flex flex-col items-center mb-6">
                        <Image src={logoSrc} alt="Company Logo" preview={false} width={100} />
                        <Title level={3} className={`mt-4 ${dark ? 'text-white' : 'text-gray-800'}`}>
                            {isLoading ? 'Loading...' : companyData?.brFname}
                        </Title>
                    </div>

                    <Steps current={currentStep} className="mb-8">
                        {steps.map(step => (
                            <Step key={step.title} title={step.title} />
                        ))}
                    </Steps>

                    <Form<FormValues>
                        form={form}
                        layout="vertical"
                        onFinish={handleSubmit}
                    >
                        {steps[currentStep].content}

                        {currentStep === 0 && (
                            <Form.Item>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    size="large"
                                    block
                                    className={`${dark ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'}`}
                                >
                                    Check Eligibility & Continue
                                </Button>
                            </Form.Item>
                        )}
                    </Form>

                    <div className="mt-6 text-center text-sm">
                        <p className={dark ? 'text-gray-400' : 'text-gray-600'}>
                            By registering myself I acknowledge that I have read and understood the
                            <br />
                            <Button type="link" onClick={showTerms} className="p-0">Terms and Conditions</Button> of using Hazina SACCOS Portal
                        </p>

                        <p className="mt-4">
                            <Link
                                to="/register"
                                className={`font-medium ${dark ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-500'}`}
                            >
                                I have Invitation Code? Complete your registration here
                            </Link>
                        </p>
                    </div>

                    <Modal
                        title="Hazina SACCOS Terms and Conditions"
                        open={isTermsModalVisible}
                        onOk={() => setIsTermsModalVisible(false)}
                        onCancel={() => setIsTermsModalVisible(false)}
                        width={800}
                        footer={[
                            <Button key="close" type="primary" onClick={() => setIsTermsModalVisible(false)}>
                                I Understand
                            </Button>
                        ]}
                    >
                        <div className={`p-4 ${dark ? 'text-gray-100' : 'text-gray-800'} max-h-96 overflow-y-auto`}>
                            <Title level={4}>HAZINA SACCOS MEMBERSHIP TERMS AND CONDITIONS</Title>

                            <Title level={5}>1. MEMBERSHIP ELIGIBILITY</Title>
                            <Paragraph>
                                <ul className="list-disc pl-5">
                                    <li><strong>Government Employment Requirement:</strong> Membership is exclusively available to government employees. Applicants must provide valid proof of government employment including official employment letter, current pay slip, and government employee identification.</li>
                                    <li><strong>Verification Process:</strong> All employment credentials will be verified with the respective government departments before membership approval.</li>
                                </ul>
                            </Paragraph>

                            <Title level={5}>2. FINANCIAL REQUIREMENTS</Title>
                            <Paragraph>
                                <ul className="list-disc pl-5">
                                    <li><strong>Share Capital:</strong> Every member is required to purchase shares worth <strong>TSH 500,000</strong> (Five Hundred Thousand Tanzanian Shillings) upon joining. This represents the member's equity stake in the cooperative.</li>
                                    <li><strong>Monthly Compulsory Savings:</strong> Members must authorize automatic monthly deductions of <strong>TSH 50,000</strong> (Fifty Thousand Tanzanian Shillings) for compulsory savings through salary deduction.</li>
                                    <li><strong>One-Third Rule:</strong> After all deductions, including SACCOS contributions, members must remain with at least one-third (1/3) of their gross monthly salary.</li>
                                    <li><strong>Payment Terms:</strong> Share capital must be paid in full before membership activation. Monthly savings deductions begin in the first month following membership approval.</li>
                                </ul>
                            </Paragraph>

                            <Title level={5}>3. LOAN PRODUCTS AND ELIGIBILITY</Title>
                            <Paragraph>
                                <ul className="list-disc pl-5">
                                    <li><strong>Loan Access:</strong> Members become eligible for various loan products based on their compliance with "Sera za Ushirika" (Cooperative Bylaws), including personal development loans, emergency loans, asset financing loans, and business development loans.</li>
                                    <li><strong>Qualification Criteria:</strong> Loan eligibility is determined by length of membership, savings history and consistency, share capital contribution, adherence to cooperative bylaws, and credit history within the cooperative.</li>
                                </ul>
                            </Paragraph>

                            <Title level={5}>4. MEMBER OBLIGATIONS</Title>
                            <Paragraph>
                                <ul className="list-disc pl-5">
                                    <li>Maintain continuous government employment</li>
                                    <li>Honor automatic deduction agreements</li>
                                    <li>Comply with all cooperative bylaws and regulations</li>
                                    <li>Attend required meetings and training sessions</li>
                                    <li>Provide accurate and updated personal information</li>
                                    <li>Immediately notify the SACCOS of changes in employment status or contact information</li>
                                </ul>
                            </Paragraph>

                            <Title level={5}>5. TERMINATION AND WITHDRAWAL</Title>
                            <Paragraph>
                                <ul className="list-disc pl-5">
                                    <li><strong>Voluntary Withdrawal:</strong> Members may withdraw by providing 30 days written notice, subject to settlement of all outstanding obligations.</li>
                                    <li><strong>Involuntary Termination:</strong> Membership may be terminated for cessation of government employment, failure to meet financial obligations, violation of cooperative bylaws, or fraudulent activities.</li>
                                </ul>
                            </Paragraph>

                            <Title level={5}>6. FEES AND CHARGES</Title>
                            <Paragraph>
                                <ul className="list-disc pl-5">
                                    <li>Registration fees and initial deposits may be subject to processing charges</li>
                                    <li>If an application is rejected or withdrawn after submission, certain charges may apply and may not be fully refundable</li>
                                    <li>All applicable fees will be clearly communicated during the registration process</li>
                                </ul>
                            </Paragraph>

                            <Title level={5}>7. DATA PRIVACY AND SECURITY</Title>
                            <Paragraph>
                                <ul className="list-disc pl-5">
                                    <li>By submitting this application, you consent to Hazina SACCOS collecting, processing, and storing your personal and financial information</li>
                                    <li>Your information will be used for evaluating membership eligibility and providing SACCOS services</li>
                                    <li>Hazina SACCOS will protect your data in accordance with applicable data protection laws</li>
                                    <li>Members are responsible for maintaining the confidentiality of their portal login credentials</li>
                                </ul>
                            </Paragraph>

                            <Title level={5}>8. DISPUTE RESOLUTION</Title>
                            <Paragraph>
                                <ul className="list-disc pl-5">
                                    <li><strong>Internal Resolution:</strong> Disputes shall first be addressed through internal cooperative mechanisms as outlined in the Sera za Ushirika</li>
                                    <li><strong>Legal Jurisdiction:</strong> These terms are governed by the laws of the United Republic of Tanzania</li>
                                </ul>
                            </Paragraph>

                            <Divider />

                            <Paragraph strong className="text-center">
                                By accepting these terms and conditions, you acknowledge that you have read, understood, and agree to be bound by all the provisions outlined above. Continued use of services after amendments constitutes acceptance of new terms.
                            </Paragraph>
                        </div>
                    </Modal>
                </Card>
            </div>
        </div>
    );
};

export default SelfInvitationForm;